import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { api } from "./lib/api";
import { TopBar } from "./components/TopBar";
import { BottomNav } from "./components/BottomNav";
import { AccountSheet } from "./components/AccountSheet";
import { AssistantSheet } from "./components/AssistantSheet";
import LoginPage from "./pages/LoginPage";
import VenuesPage from "./pages/VenuesPage";
import AgendaPage from "./pages/AgendaPage";
import TodayPage from "./pages/TodayPage";
import FissaPage from "./pages/FissaPage";
import SearchPage from "./pages/SearchPage";
import "./App.css";

function AppRoutes() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountPersonId, setAccountPersonId] = useState(null);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [todayCount, setTodayCount] = useState(0);
  const location = useLocation();

  const refreshSession = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setSession(data);
    } catch {
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshSession(); }, [refreshSession]);
  useEffect(() => {
    if (!session) return undefined;
    const refresh = () => api.get("/today/count").then(({ data }) => setTodayCount(data.count)).catch(() => {});
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    refresh(); window.addEventListener("focus", refresh); window.addEventListener("luchador-tasks-updated", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => { window.removeEventListener("focus", refresh); window.removeEventListener("luchador-tasks-updated", refresh);
      document.removeEventListener("visibilitychange", onVisible); };
  }, [session, location.pathname]);
  useEffect(() => {
    const onPerson = (event) => { setAccountPersonId(event.detail.id); setAccountOpen(true); };
    window.addEventListener("luchador-open-team-person", onPerson);
    return () => window.removeEventListener("luchador-open-team-person", onPerson);
  }, []);

  if (loading) {
    return <div className="loading-screen" data-testid="session-loading">LUCHADOR<span className="loading-line" /></div>;
  }
  if (!session) {
    return <Routes><Route path="/login" element={<LoginPage />} /><Route path="*" element={<Navigate to="/login" replace />} /></Routes>;
  }

  const isSalesperson = session.user.role === "commerciale";
  const lastRoute = isSalesperson ? "/fissa" : "/cerca";

  return (
    <div className="app-frame" data-testid="authenticated-app">
      <TopBar user={session.user} onAccount={() => setAccountOpen(true)} />
      <main className="main-content" data-testid="main-content" key={location.pathname}>
        <Routes>
          <Route path="/oggi" element={<TodayPage session={session} />} />
          <Route path="/agenda" element={<AgendaPage session={session} />} />
          {!isSalesperson && <Route path="/cerca" element={<SearchPage session={session} />} />}
          <Route path="/locali" element={<VenuesPage session={session} />} />
          {isSalesperson && <Route path="/fissa" element={<FissaPage session={session} />} />}
          <Route path="*" element={<Navigate to="/oggi" replace />} />
        </Routes>
      </main>
      <BottomNav lastRoute={lastRoute} todayCount={todayCount} onAssistant={() => setAssistantOpen(true)} />
      <AccountSheet open={accountOpen} initialPersonId={accountPersonId}
        onOpenChange={(value) => { setAccountOpen(value); if (!value) setAccountPersonId(null); }}
        session={session} onLogout={() => { setSession(null); setAccountOpen(false); }} />
      <AssistantSheet open={assistantOpen} onOpenChange={setAssistantOpen} />
    </div>
  );
}

export default function App() {
  return <BrowserRouter><AppRoutes /></BrowserRouter>;
}